import React, { useState, useRef, useCallback } from 'react';
import FailoverImg from './FailoverImg';
import { toJpegOrPng } from '../utils/imageUpload';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Sortable thumbnail wrapper
function SortablePhotoItem({ photo, index, isNew, onPromote, onDelete, onAddNew }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isNew) {
    return (
      <button
        type="button"
        onClick={onAddNew}
        className="w-16 h-16 flex-shrink-0 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:border-indigo-500 hover:text-indigo-500 transition-colors text-2xl font-light"
      >
        +
      </button>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="w-16 h-16 flex-shrink-0 relative group cursor-pointer"
      {...attributes}
      {...listeners}
      onClick={() => onPromote(index)}
    >
      <FailoverImg
        src={photo.url}
        alt={`Photo ${index + 1}`}
        className="w-16 h-16 object-cover rounded-lg border-2 border-transparent group-hover:border-indigo-500 transition-colors"
      />
      {/* Delete overlay on hover */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(photo.id);
        }}
        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow"
        title="Delete photo"
      >
        ×
      </button>
      {/* Drag handle indicator */}
      <div className="absolute bottom-0.5 left-0.5 opacity-0 group-hover:opacity-60 transition-opacity">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="white">
          <circle cx="3" cy="3" r="1.5" />
          <circle cx="9" cy="3" r="1.5" />
          <circle cx="3" cy="9" r="1.5" />
          <circle cx="9" cy="9" r="1.5" />
        </svg>
      </div>
    </div>
  );
}

export default function PhotoEditorModal({ isOpen, onClose, photos = [], onSave, onUpload }) {
  const [localPhotos, setLocalPhotos] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize local state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      const normalized = (photos || []).map((p, i) => ({
        id: p.id || `photo-${Date.now()}-${i}`,
        url: p.url || p,
      }));
      setLocalPhotos(normalized);
      setHeroIndex(normalized.length > 0 ? 0 : -1);
    }
  }, [isOpen, photos]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setLocalPhotos((prev) => {
        const oldIndex = prev.findIndex((p) => p.id === active.id);
        const newIndex = prev.findIndex((p) => p.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          const next = arrayMove(prev, oldIndex, newIndex);
          // Update hero index if the hero photo was moved
          setHeroIndex((prevHero) => {
            if (prevHero === oldIndex) return newIndex;
            if (prevHero === newIndex) return oldIndex;
            return prevHero;
          });
          return next;
        }
        return prev;
      });
    }
  }, []);

  const handlePromote = (index) => {
    setHeroIndex(index);
  };

  const handleDelete = (id) => {
    setLocalPhotos((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      const next = prev.filter((p) => p.id !== id);
      setHeroIndex((prevHero) => {
        if (prevHero === idx) {
          return next.length > 0 ? Math.min(idx, next.length - 1) : -1;
        }
        if (prevHero > idx) return prevHero - 1;
        return prevHero;
      });
      return next;
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Use the provided onUpload callback if available
    if (onUpload) {
      try {
        const result = await onUpload(file);
        if (result?.url) {
          const newPhoto = {
            id: `photo-${Date.now()}`,
            url: result.url,
          };
          setLocalPhotos((prev) => [...prev, newPhoto]);
        }
      } catch (err) {
        console.error('Error uploading photo:', err);
      }
    } else {
      // Fallback: direct upload
      setUploading(true);
      try {
        // Phones often hand over .webp; convert to JPEG so only jpg/png are uploaded
        const uploadFile = await toJpegOrPng(file);
        const formData = new FormData();
        formData.append('file', uploadFile);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/uploads?folder=transactions`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          const newPhoto = {
            id: `photo-${Date.now()}`,
            url: data.url,
          };
          setLocalPhotos((prev) => [...prev, newPhoto]);
        }
      } catch (err) {
        console.error('Error uploading photo:', err);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = () => {
    const photosArray = localPhotos.map((p) => ({ url: p.url }));
    onSave(photosArray);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  const heroPhoto = heroIndex >= 0 && localPhotos[heroIndex] ? localPhotos[heroIndex] : null;
  // Filter out the "+" placeholder from sortable items
  const sortablePhotos = localPhotos;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
      <div className="bg-white rounded-lg shadow-xl w-[80vw] h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Edit Photos</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Hero Preview */}
          <div className="mb-6">
            <div className="w-full h-[50vh] bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center relative">
              {heroPhoto ? (
                <FailoverImg
                  src={heroPhoto.url}
                  alt="Hero preview"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <svg
                    className="mx-auto mb-2"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <p className="text-sm">No photo selected</p>
                  <p className="text-xs mt-1">Upload a photo or click + below</p>
                </div>
              )}
            </div>
            {heroPhoto && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Primary photo — click a thumbnail below to change
              </p>
            )}
          </div>

          {/* Thumbnail Carousel */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">
                Photos ({localPhotos.length})
              </h3>
              <label className="text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer">
                {uploading ? 'Uploading...' : '+ Upload'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>

            {sortablePhotos.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                No photos yet — upload one to get started
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortablePhotos.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {sortablePhotos.map((photo, index) => (
                      <SortablePhotoItem
                        key={photo.id}
                        photo={photo}
                        index={index}
                        isNew={false}
                        onPromote={handlePromote}
                        onDelete={handleDelete}
                      />
                    ))}
                    {/* Add button at the end */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-16 h-16 flex-shrink-0 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:border-indigo-500 hover:text-indigo-500 transition-colors text-2xl font-light disabled:opacity-50"
                    >
                      {uploading ? '⋯' : '+'}
                    </button>
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
